/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import { CircularProgress, Grid } from '@mui/material'
import Typography from '@mui/material/Typography'
import React, { useEffect } from 'react'
import { useAsyncIntervalFetch } from 'react-hooks-async-handlers'
import { useSelector } from 'react-redux'
import { RendererStatusSplit } from 'react-renderer-status-split'
import { StepComponentProps } from 'react-step-builder'
import analytics from '../../libs/analytics'
import api from '../../libs/api'
import { ReduxState } from '../../reducers'
import PhonePreview from './components/PhonePreview'
import useOnboardAction from './hooks/useOnboardAction'
import IdentityCodePreview from './identity/IdentityCodePreview'

const POLING_INTERVAL = 5000 // m.sec.

function StepIdentityCheck(props: StepComponentProps) {
  const { next } = props
  const { phoneInfo, phone_number } = useSelector((state: ReduxState) => ({
    phoneInfo: state.phone,
    phone_number: state.form.phone_number,
  }))
  const code = phoneInfo.verification_code || 'XXXX'
  const onboardAction = useOnboardAction({
    ...(props.state as any),
    identity_verification_id: phoneInfo.verification_id,
  })

  useAsyncIntervalFetch(async () => {
    if (document.hidden) return null // no need to spam on background
    if (onboardAction.isErrored || onboardAction.isDone) return null
    const { verification_id } = phoneInfo
    if (!verification_id) {
      console.error('verification_id is null. Seems like a bug')
      return null
    }

    const response = await api.onboardIdentityCheckProgressCreate({ id: verification_id })
    if (response.data.status) {
      /**
       * Server send user auth request. Here we wait when user will approve it.
       */
      await onboardAction.execute()
      next()
    }
  }, POLING_INTERVAL)

  useEffect(() => {
    analytics.sendEvent('page@StepIdentityCheck')
  }, [])

  const handleBackToPhone = () => props.jump(5)

  return (
    <Grid container direction="column" alignItems="center">
      <Typography sx={{ fontSize: '21px', color: '#352E73', textAlign: 'center', mt: 2, fontWeight: 600 }}>
        It’s seems like there’s already an account for your phone number...
      </Typography>

      <PhonePreview
        phone_number={phone_number}
        onRequestChange={handleBackToPhone}
        disabled={onboardAction.isLoading}
      />

      <Typography
        textAlign={'center'}
        css={css`
          font-size: 21px;
          color: #352e73;
          font-weight: bold;
          margin-bottom: 30px;
          padding: 0 30px;
        `}
      >
        You can be connected only to 1 company at a time. To connect your existing account with the company you just
        created:
      </Typography>

      <RendererStatusSplit
        statuses={onboardAction}
        renderError={(error) => <Typography color={'error'}>{error}</Typography>}
        renderLoading={() => <CircularProgress />}
        render={() => <IdentityCodePreview code={code} />}
      />
    </Grid>
  )
}

export default StepIdentityCheck
